// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useMemo } from 'react';

import { Text } from '@/components/Themed';
import { type AvailableMember } from '@/components/PlayerMembershipEditor';
import GhostAvatar from '@/components/GhostAvatar';

export type AddedAiPlayer = { id: string; name: string };

type Props = {
  visible: boolean;
  /** People a ghost can be based on: the current user's friends (the user themself has a dedicated button). */
  ghostCandidates: AvailableMember[];
  /** Source player ids that already have a ghost in the round, to mark them as added. */
  ghostedSourceIds: string[];
  addedAiPlayers: AddedAiPlayer[];
  busy?: boolean;
  title: string;
  addedLabel: string;
  pureAiLabel: string;
  ghostOfMeLabel: string;
  ghostOfFriendLabel: string;
  noResults: string;
  closeLabel: string;
  doneLabel: string;
  onCreatePure: () => void;
  onGhostOfMe: () => void;
  onGhostOfFriend: (member: AvailableMember) => void;
  onRemoveAiPlayer: (player: AddedAiPlayer) => void;
  onClose: () => void;
};

export default function AddAiPlayerModal({
  visible,
  ghostCandidates,
  ghostedSourceIds,
  addedAiPlayers,
  busy = false,
  title,
  addedLabel,
  pureAiLabel,
  ghostOfMeLabel,
  ghostOfFriendLabel,
  noResults,
  closeLabel,
  doneLabel,
  onCreatePure,
  onGhostOfMe,
  onGhostOfFriend,
  onRemoveAiPlayer,
  onClose,
}: Props) {
  const ghostedIds = useMemo(() => new Set(ghostedSourceIds), [ghostedSourceIds]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => null}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>{title}</Text>
            <Pressable style={styles.closeButton} onPress={onClose} accessibilityLabel={closeLabel}>
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>

          {addedAiPlayers.length > 0 ? (
            <View style={styles.addedSection}>
              <Text style={styles.addedLabel}>{`${addedLabel} (${addedAiPlayers.length})`}</Text>
              <View style={styles.chips}>
                {addedAiPlayers.map((player) => (
                  <Pressable
                    key={player.id}
                    style={styles.chip}
                    onPress={() => onRemoveAiPlayer(player)}
                    accessibilityLabel={player.name}
                  >
                    <GhostAvatar size={18} />
                    <Text style={styles.chipText} numberOfLines={1}>
                      {player.name}
                    </Text>
                    <Text style={styles.chipRemove}>✕</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}

          <Pressable
            style={[styles.primaryButton, busy && styles.buttonDisabled]}
            onPress={onCreatePure}
            disabled={busy}
            accessibilityLabel={pureAiLabel}
          >
            <Text style={styles.primaryButtonText}>{`+  ${pureAiLabel}`}</Text>
          </Pressable>
          <Pressable
            style={[styles.secondaryButton, busy && styles.buttonDisabled]}
            onPress={onGhostOfMe}
            disabled={busy}
            accessibilityLabel={ghostOfMeLabel}
          >
            <GhostAvatar size={20} />
            <Text style={styles.secondaryButtonText}>{ghostOfMeLabel}</Text>
          </Pressable>

          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>{ghostOfFriendLabel}</Text>
            <View style={styles.dividerLine} />
          </View>

          <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
            {ghostCandidates.length === 0 ? (
              <Text style={styles.empty}>{noResults}</Text>
            ) : (
              ghostCandidates.map((member) => {
                const added = ghostedIds.has(member.id);
                return (
                  <Pressable
                    key={member.id}
                    style={[styles.friendRow, added && styles.friendRowAdded]}
                    onPress={() => (added || busy ? null : onGhostOfFriend(member))}
                    accessibilityLabel={member.name}
                    accessibilityState={{ selected: added }}
                  >
                    <GhostAvatar size={32} />
                    <Text style={styles.friendName} numberOfLines={1}>
                      {member.name}
                    </Text>
                    <View style={[styles.addIcon, added && styles.addedIcon]}>
                      <Text style={[styles.addIconText, added && styles.addedIconText]}>{added ? '✓' : '+'}</Text>
                    </View>
                  </Pressable>
                );
              })
            )}
          </ScrollView>

          <Pressable style={styles.doneButton} onPress={onClose} accessibilityLabel={doneLabel}>
            <Text style={styles.doneButtonText}>{doneLabel}</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    maxHeight: '82%',
    backgroundColor: '#07200D',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 16,
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: {
    flex: 1,
    color: '#F5F5DC',
    fontSize: 18,
    fontWeight: '700',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: '#1A3A20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    color: '#8FA08B',
    fontSize: 16,
    fontWeight: '700',
  },
  addedSection: {
    gap: 8,
  },
  addedLabel: {
    color: '#8FA08B',
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingLeft: 10,
    paddingRight: 10,
    borderRadius: 999,
    backgroundColor: '#123018',
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  chipText: {
    color: '#F5F5DC',
    fontSize: 13,
    fontWeight: '700',
    maxWidth: 150,
  },
  chipRemove: {
    color: '#A1D493',
    fontSize: 13,
    fontWeight: '700',
  },
  primaryButton: {
    backgroundColor: '#A1D493',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    color: '#011806',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  secondaryButton: {
    flexDirection: 'row',
    gap: 8,
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButtonText: {
    color: '#A1D493',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#1A3A20',
  },
  dividerText: {
    color: '#8FA08B',
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  list: {
    maxHeight: 240,
  },
  empty: {
    color: '#8FA08B',
    fontSize: 13,
    textAlign: 'center',
    paddingVertical: 16,
  },
  friendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  friendRowAdded: {
    backgroundColor: '#0D2A13',
  },
  friendName: {
    flex: 1,
    color: '#F5F5DC',
    fontSize: 14,
  },
  addIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIconText: {
    color: '#A1D493',
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 17,
  },
  addedIcon: {
    backgroundColor: '#A1D493',
    borderColor: '#A1D493',
  },
  addedIconText: {
    color: '#011806',
  },
  doneButton: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneButtonText: {
    color: '#A1D493',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
});
